import { useEffect, useState } from "react";
import { toast } from "sonner";
import { DatabaseBackup, Download, Users, Scissors, Receipt, CalendarDays, FileText } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const norm = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
const num = (v) => String(Number(v) || 0).replace(".", ",");
const hoje = () => new Date().toISOString().slice(0, 10);

function toCsv(headers, rows) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = [headers.map(esc).join(";"), ...rows.map((r) => r.map(esc).join(";"))];
  return "\uFEFF" + lines.join("\r\n");
}

function download(name, content) {
  const url = URL.createObjectURL(new Blob([content], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

const SITUACAO = (s) =>
  s.status === "paga" ? "Paga" : s.status === "cancelada" ? "Cancelada" : s.paid ? "Fiado (pago)" : "Fiado (pendente)";
const STATUS_AG = (s) => (s === "concluido" ? "Concluído" : s === "agendado" ? "Agendado" : s || "");

export default function Backup() {
  const [data, setData] = useState({ clients: [], services: [], bills: [], appointments: [], sales: [] });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/clients").then((r) => r.data).catch(() => []),
      api.get("/services").then((r) => r.data).catch(() => []),
      api.get("/bills").then((r) => r.data).catch(() => []),
      api.get("/appointments").then((r) => r.data).catch(() => []),
      api.get("/sales").then((r) => r.data).catch(() => []),
    ]).then(([clients, services, bills, appointments, sales]) => setData({ clients, services, bills, appointments, sales }));
  }, []);

  const sheets = [
    {
      key: "clientes", label: "Clientes", icon: Users, rows: data.clients,
      build: (cs) => ({
        headers: ["Nome", "Telefone", "Observações"],
        rows: cs.map((c) => [c.name, c.phone || "", c.notes || ""]),
      }),
    },
    {
      key: "servicos", label: "Serviços", icon: Scissors, rows: data.services,
      build: (ss) => ({
        headers: ["Nome", "Preço (R$)"],
        rows: ss.map((s) => [s.name, num(s.price)]),
      }),
    },
    {
      key: "pagar", label: "Contas a Pagar", icon: Receipt, rows: data.bills,
      build: (bs) => ({
        headers: ["Vencimento", "Doc", "Obrigação", "Fornecedor", "Valor (R$)", "Data Pgto", "Recorrente"],
        rows: bs.map((b) => [b.venc_date, b.doc || "", b.obrigacao || "", b.fornecedor || "", num(b.valor), b.dt_pgto || "", b.is_recurring ? "Sim" : "Não"]),
      }),
    },
    {
      key: "agendamentos", label: "Agendamentos", icon: CalendarDays, rows: data.appointments,
      build: (as_) => ({
        headers: ["Data", "Hora", "Cliente", "Telefone", "Serviço", "Observações", "Status"],
        rows: as_.map((a) => [a.date, a.time, a.client_name, a.phone || "", a.service || "", a.notes || "", STATUS_AG(a.status)]),
      }),
    },
    {
      key: "vendas", label: "Vendas", icon: FileText, rows: data.sales,
      build: (ss) => ({
        headers: ["Data", "Cliente", "Itens", "Desconto (R$)", "Total (R$)", "Situação"],
        rows: ss.map((s) => [
          s.sale_date, s.client_name,
          (s.items || []).map((i) => `${i.name} (R$ ${num(i.price)})`).join(" + "),
          num(s.discount), num(s.total), SITUACAO(s),
        ]),
      }),
    },
  ];

  const baixar = (sheet) => {
    const { headers, rows } = sheet.build(sheet.rows);
    download(`${sheet.key}-${hoje()}.csv`, toCsv(headers, rows));
  };

  const baixarTudo = async () => {
    setBusy(true);
    try {
      for (const sheet of sheets) {
        baixar(sheet);
        await new Promise((r) => setTimeout(r, 400));
      }
      toast.success("Planilhas baixadas");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="backup-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-light">Backup</h1>
          <p className="text-sm text-stone-500 mt-1">Baixe seus cadastros em planilhas (CSV) para guardar ou abrir no Excel.</p>
        </div>
        <button
          onClick={baixarTudo}
          disabled={busy || sheets.every((s) => !s.rows.length)}
          className="flex items-center gap-2 px-7 py-3 rounded-full bg-[#121212] text-white text-sm font-medium hover:bg-stone-700 transition-colors disabled:opacity-50"
          data-testid="backup-download-all"
        >
          <DatabaseBackup size={16} /> {busy ? "Baixando…" : "Baixar tudo"}
        </button>
      </div>

      <div className="bg-white border border-stone-200 rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.03)] divide-y divide-stone-100 max-w-2xl">
        {sheets.map((sheet) => (
          <div key={sheet.key} className="flex items-center justify-between gap-4 px-5 py-4">
            <div className="flex items-center gap-3 min-w-0">
              <span className="w-9 h-9 rounded-full bg-[#F3EFE6] text-[#8a6d4b] flex items-center justify-center shrink-0">
                <sheet.icon size={16} />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-medium" data-testid={`backup-label-${sheet.key}`}>{sheet.label}</p>
                <p className="text-xs text-stone-400">{sheet.rows.length} registro{sheet.rows.length === 1 ? "" : "s"}</p>
              </div>
            </div>
            <button
              onClick={() => baixar(sheet)}
              disabled={!sheet.rows.length}
              className="flex items-center gap-2 px-4 py-2 rounded-full border border-stone-300 text-sm text-stone-600 hover:border-[#C8A279] hover:text-[#121212] transition-colors disabled:opacity-40 disabled:hover:border-stone-300 disabled:hover:text-stone-600"
              data-testid={`backup-download-${sheet.key}`}
            >
              <Download size={14} /> Planilha
            </button>
          </div>
        ))}
      </div>

      <p className="text-xs text-stone-400 mt-4 max-w-2xl">
        Dica: guarde as planilhas em um lugar seguro (e-mail, nuvem ou computador). O backup não sai do seu navegador — os dados vêm do painel na hora do download.
      </p>
    </div>
  );
}
